import { pushToDataLayer } from './PushToDataLayer'
import { camelCaseToSnakeCase } from '../utils/stringHelpers'

export function trackCustomSelectDropdown (selectElement) {
  const selectedValue = selectElement.options[selectElement.selectedIndex].text
  const dataset = { ...selectElement.dataset, selectedOption: selectedValue }

  trackCustomEvent(dataset, 'customClick', 'customClickName')
}

export function trackCustomClick (element) {
  const dataset = element.dataset
  trackCustomEvent(dataset, 'customClick', 'customClickName')
}

export function trackCustomClickInAmplitude (element) {
  const { amplitudeComponentName, amplitudeEventName, ...rest } = element.dataset
  if (!amplitudeComponentName && !amplitudeEventName) return

  const dataLayerAttributes = {
    // amplitudeEventName is defined when we are not emitting a "[Component name] Clicked", mostly for older events as "Clicked" is the preferred format.
    amplitude_event_name: amplitudeEventName || getAmplitudeClickEventName(amplitudeComponentName),
    amplitude_event_properties: amplitudeEventProperties(rest)
  }

  // TODO (Joj): change this to 'amplitudeCustomEvent (Vinterior)' in the next PR - changing in GTM at the same time.
  pushToDataLayer('amplitudeCustomClickTracking (Vinterior)', dataLayerAttributes)
}

function getAmplitudeClickEventName (amplitudeComponentName) {
  // Temporary solution as Amplitude is already receiveing data with this specific event name. Just waiting for Viktoria
  // to sign off changing it to 'Log In/Register Clicked', then we can remove this.
  if (amplitudeComponentName === 'Log In/Register Click') return amplitudeComponentName

  return amplitudeComponentName + ' Clicked'
}

// TODO: Update the above click tracking and this one in GTM to be "generic" and not "click"
// Make use of the generic amplitude_event_name and amplitude_event_properties in the Data Layer
export function trackAmplitudeEvent (eventName, eventProperties) {
  const dataLayerAttributes = {
    amplitude_event_name: eventName,
    amplitude_event_properties: eventProperties
  }

  // TODO (Joj): change this to 'amplitudeCustomEvent (Vinterior)' in the next PR - changing in GTM at the same time.
  pushToDataLayer('amplitudeCustomClickTracking (Vinterior)', dataLayerAttributes)
}

const EXPOSURE_PROPERTY_PREFIX = 'amplitudeExperimentProperty'
const EXPOSURE_USER_PROPERTY_PREFIX = 'amplitudeExperimentUserProperty'

// data-amplitude-experiment-property-* attributes ride on the exposure event and
// data-amplitude-experiment-user-property-* are set on the Amplitude user, so an
// experiment can be split finer than its variant (e.g. by session intent).
export function trackAmplitudeExperimentExposure (element) {
  const { amplitudeExperimentFlagKey, amplitudeExperimentVariant } = element.dataset

  if (amplitudeExperimentFlagKey && amplitudeExperimentVariant) {
    const dataLayerAttributes = {
      amplitude_event_properties: {
        flag_key: amplitudeExperimentFlagKey,
        variant: amplitudeExperimentVariant,
        ...prefixedDatasetProperties(element.dataset, EXPOSURE_PROPERTY_PREFIX)
      }
    }

    pushToDataLayer('amplitudeExperimentExposure (Vinterior)', dataLayerAttributes)
    setAmplitudeUserProperties(prefixedDatasetProperties(element.dataset, EXPOSURE_USER_PROPERTY_PREFIX))
  }
}

function prefixedDatasetProperties (dataset, prefix) {
  return Object.keys(dataset).reduce((properties, key) => {
    if (!key.startsWith(prefix) || key === prefix) return properties

    const name = key.slice(prefix.length)
    properties[camelCaseToSnakeCase(name.charAt(0).toLowerCase() + name.slice(1))] = dataset[key]
    return properties
  }, {})
}

// The Amplitude SDK is loaded by GTM; with no SDK yet there is nothing to set.
function setAmplitudeUserProperties (properties) {
  const names = Object.keys(properties)
  if (names.length === 0 || !window.amplitude?.Identify || !window.amplitude.identify) return

  const identify = new window.amplitude.Identify()
  names.forEach(name => identify.set(name, properties[name]))
  window.amplitude.identify(identify)
}

export function trackCustomComponentImpression (element) {
  const dataset = element.dataset
  trackCustomEvent(dataset, 'customComponentImpression', 'customComponentName')

  // The same data-amplitude-* properties a click sends, so Viewed and Clicked join on them.
  const { amplitudeComponentName, amplitudeEventName, ...rest } = dataset
  if (amplitudeComponentName) trackAmplitudeEvent(amplitudeComponentName + ' Viewed', amplitudeEventProperties(rest))
}

export function trackStandardCarouselImpression (element) {
  const dataset = element.dataset
  const { amplitudeComponentName } = dataset
  const currentScreenWidth = window.innerWidth

  const fullCarouselListings = Array.from(element.children)

  const initiallyVisibleOnDesktopListings = fullCarouselListings.slice(0, 4)
  const initiallyVisibleOnMobileListings = fullCarouselListings.slice(0, 2)
  const initiallyVisibleListings = currentScreenWidth > 600 ? initiallyVisibleOnDesktopListings : initiallyVisibleOnMobileListings
  const initiallyVisibleListingsIds = initiallyVisibleListings.map(listing => listing.dataset.id)

  if (amplitudeComponentName) {
    trackAmplitudeEvent(amplitudeComponentName + ' Viewed', {
      initially_visible_listing_ids: initiallyVisibleListingsIds,
      full_carousel_listing_ids: fullCarouselListings.map(listing => listing.dataset.id)
    })
  }
}

export function trackRecentItemsCarouselImpression (element) {
  const dataset = element.dataset
  const { amplitudeComponentName } = dataset
  const currentScreenWidth = window.innerWidth
  const fullCarouselListings = Array.from(element.children)

  const breakpointConfig = [
    { width: 480, tiles: 2 },
    { width: 600, tiles: 3 },
    { width: 887, tiles: 4 },
    { width: 1199, tiles: 5 },
    { width: 1400, tiles: 6 }
  ]

  const currentNumberOfTiles = breakpointConfig
    .findLast(config => currentScreenWidth >= config.width)?.tiles || 6

  const initiallyVisibleListings = fullCarouselListings.slice(0, currentNumberOfTiles + 1)
  const initiallyVisibleListingsIds = initiallyVisibleListings.map(listing => listing.dataset.id)

  if (amplitudeComponentName) {
    trackAmplitudeEvent(amplitudeComponentName + ' Viewed', {
      initially_visible_listing_ids: initiallyVisibleListingsIds,
      full_carousel_listing_ids: fullCarouselListings.map(listing => listing.dataset.id)
    })
  }
}

function trackCustomEvent (dataset, eventName, snowplowPropertyName) {
  const dataLayerAttributes = { custom_properties: createCustomProperties(dataset, snowplowPropertyName) }

  dataLayerAttributes[camelCaseToSnakeCase(snowplowPropertyName)] = dataset[snowplowPropertyName]

  pushToDataLayer(`${eventName} (Vinterior)`, dataLayerAttributes)
}

function createCustomProperties (dataset, snowplowPropertyName) {
  // returns array like [{name: 'listing_id', value: 1}, {name: 'enquiry', value: 'general'}]
  const customPropertiesObject = Object.assign({}, dataset)
  delete customPropertiesObject[snowplowPropertyName]
  return Object.keys(customPropertiesObject).map(key => { return { name: camelCaseToSnakeCase(key), value: customPropertiesObject[key] } })
}

export function amplitudeEventProperties (dataset) {
  const properties = Object
    .keys(dataset)
    .filter(key => isAmplitudeDataVariable(key))
    .map(key => ({ [formatAmplitudeEventPropertyKey(key)]: dataset[key] }))

  return Object.assign({}, ...properties)
}

function isAmplitudeDataVariable (key) {
  // Temporary solution so we don't add data-amplitude-component-names to sent amplitude event properties
  // TODO Joj: Will find a more elegant solution for this
  if (key === 'amplitudeComponentName') return
  return key.startsWith('amplitude')
}

function formatAmplitudeEventPropertyKey (key) {
  return camelCaseToSnakeCase(key).slice(10)
}
